import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dashboardAPI as API } from '../../../axiosConfig';

const SLICE_NAME = 'search';
const GET_ORDER = `${SLICE_NAME}/order`;
const GET_TRANSACTIONS = `${SLICE_NAME}/order/transactions`;
const GET_REFUNDS = `${SLICE_NAME}/order/refunds`;

const GET_TRANSACTION_DETAILS = `${SLICE_NAME}/transaction`;
const GET_REFUND_DETAILS = `${SLICE_NAME}/refund`;

const GET_CASHIER_SNAPSHOT = `${SLICE_NAME}/cashier/snapshot`;
const GET_GALAXY_SNAPSHOT = `${SLICE_NAME}/galaxy/snapshot`;

export const getOrderDetails = createAsyncThunk(
  GET_ORDER,
  async (orderId, { rejectWithValue }) => {
    const params = { orderId };
    try {
      const { data } = await API.get('orderdetails/fetch/cashier/orders', {
        params,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getOrderTransactions = createAsyncThunk(
  GET_TRANSACTIONS,
  async (payload, { rejectWithValue }) => {
    const { params, database } = payload;

    try {
      const { data } = await API.get(
        `orderdetails/fetch/${database}/paymenttransactionslist`,
        {
          params,
        },
      );
      if (!data) rejectWithValue(null);
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getTransactionDetails = createAsyncThunk(
  GET_TRANSACTION_DETAILS,
  async (payload, { rejectWithValue }) => {
    const { params, database } = payload;

    try {
      const { data } = await API.get(
        `orderdetails/fetch/${database}/paymenttransaction`,
        {
          params,
        },
      );
      if (!data) rejectWithValue(null);
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getRefundTransactions = createAsyncThunk(
  GET_REFUNDS,
  async (payload, { rejectWithValue }) => {
    const { params, database } = payload;
    try {
      const { data } = await API.get(
        `orderdetails/fetch/${database}/refundlist`,
        {
          params,
        },
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getRefundDetails = createAsyncThunk(
  GET_REFUND_DETAILS,
  async (payload, { rejectWithValue }) => {
    const { params, database } = payload;
    try {
      const { data } = await API.get(
        `orderdetails/fetch/${database}/refundlist`,
        {
          params,
        },
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getCashierSnapshotDetails = createAsyncThunk(
  GET_CASHIER_SNAPSHOT,
  async (payload, { rejectWithValue }) => {
    const gatewayName = payload;
    try {
      const { data } = await API.get(
        `payments/cashier/transactionDetails/${gatewayName}`,
        {
          params: payload,
        },
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getGalaxySnapshotDetails = createAsyncThunk(
  GET_GALAXY_SNAPSHOT,
  async (payload, { rejectWithValue }) => {
    const gatewayName = payload;
    try {
      const { data } = await API.get(
        `payments/galaxy/transactionDetails/${gatewayName}`,
        {
          params: payload,
        },
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const searchOrderSlice = createSlice({
  name: SLICE_NAME,
  initialState: {
    searchParams: {},
    isOrderDetailsLoading: false,
    isOrderDetailsError: false,
    orderDetails: null,
    orderDetailsError: '',

    isPaymentTransactionsLoading: false,
    isPaymentTransactionsError: false,
    paymentTransactions: null,
    paymentTransactionsError: '',

    isRefundTransactionsLoading: false,
    isRefundTransactionsError: false,
    refundTransactions: null,
    refundTransactionsError: '',
  },
  reducers: {
    setSearchParams: (state, { payload }) => {
      state.searchParams = payload;
    },
    resetOrder: (state) => {
      state.isOrderDetailsLoading = false;
      state.isOrderDetailsError = false;
      state.orderDetails = null;
      state.orderDetailsError = '';
    },
    resetTransactions: (state) => {
      state.isPaymentTransactionsLoading = false;
      state.isPaymentTransactionsError = false;
      state.paymentTransactions = null;
      state.paymentTransactionsError = '';
    },
    resetRefunds: (state) => {
      state.isRefundTransactionsLoading = false;
      state.isRefundTransactionsError = false;
      state.refundTransactions = null;
      state.refundTransactionsError = '';
    },
  },
  extraReducers: {
    // ORDER DETAILS
    [getOrderDetails.pending]: (state) => {
      state.isOrderDetailsLoading = true;
      state.isOrderDetailsError = false;
      state.orderDetailsError = '';
      state.orderDetails = null;
    },
    [getOrderDetails.rejected]: (state, { payload }) => {
      state.isOrderDetailsLoading = false;
      state.isOrderDetailsError = false;
      state.orderDetailsError = payload.message;
      state.orderDetails = null;
    },
    [getOrderDetails.fulfilled]: (state, { payload }) => {
      state.isOrderDetailsLoading = false;

      if (payload.status !== 'SUCCESS' || !payload?.data) {
        state.orderDetailsError = 'No order details found.';
        state.isOrderDetailsError = true;
        state.orderDetails = null;
        return;
      }

      state.isOrderDetailsError = false;
      state.orderDetails = payload.data;
    },

    // TRANSACTION LIST
    [getOrderTransactions.pending]: (state) => {
      state.isPaymentTransactionsLoading = true;
      state.isPaymentTransactionsError = false;
      state.paymentTransactionsError = '';
      state.paymentTransactions = null;
    },
    [getOrderTransactions.rejected]: (state, { payload }) => {
      state.isPaymentTransactionsLoading = false;
      state.isPaymentTransactionsError = true;
      state.paymentTransactionsError = payload.message;
      state.paymentTransactions = null;
    },
    [getOrderTransactions.fulfilled]: (state, { payload }) => {
      state.isPaymentTransactionsLoading = false;

      if (payload.status !== 'SUCCESS' || !payload?.data) {
        state.isPaymentTransactionsError = true;
        state.paymentTransactionsError = 'No transaction data found.';
        state.paymentTransactions = null;
        return;
      }

      state.isPaymentTransactionsError = false;
      state.paymentTransactions = payload.data;
    },

    // TRANSACTION DETAILS
    [getTransactionDetails.pending]: (state) => {
      state.isPaymentTransactionsLoading = true;
      state.isPaymentTransactionsError = false;
      state.paymentTransactionsError = '';
      state.paymentTransactions = null;
    },
    [getTransactionDetails.rejected]: (state, { payload }) => {
      state.isPaymentTransactionsLoading = false;
      state.isPaymentTransactionsError = true;
      state.paymentTransactionsError = payload.message;
      state.paymentTransactions = null;
    },
    [getTransactionDetails.fulfilled]: (state, { payload }) => {
      state.isPaymentTransactionsLoading = false;

      if (payload.status !== 'SUCCESS' || !payload?.data) {
        state.isPaymentTransactionsError = true;
        state.paymentTransactionsError = 'No transaction data found.';
        state.paymentTransactions = null;
        return;
      }

      state.isPaymentTransactionsError = false;
      state.paymentTransactions = payload.data;
    },

    // REFUND LIST
    [getRefundTransactions.pending]: (state) => {
      state.isRefundTransactionsLoading = true;
      state.isRefundTransactionsError = false;
      state.refundTransactionsError = '';
      state.refundTransactions = null;
    },
    [getRefundTransactions.rejected]: (state, { payload }) => {
      state.isRefundTransactionsLoading = false;
      state.isRefundTransactionsError = true;
      state.refundTransactionsError = payload.message;
      state.refundTransactions = null;
    },
    [getRefundTransactions.fulfilled]: (state, { payload }) => {
      state.isRefundTransactionsLoading = false;

      if (payload.status !== 'SUCCESS' || !payload?.data) {
        state.isRefundTransactionsError = true;
        state.refundTransactionsError = 'No refund transaction found';
        state.refundTransactions = null;
        return;
      }

      state.isRefundTransactionsError = false;
      state.refundTransactions = payload.data;
    },

    // REFUND DETAILS
    [getRefundDetails.pending]: (state) => {
      state.isRefundTransactionsLoading = true;
      state.isRefundTransactionsError = false;
      state.refundTransactionsError = '';
      state.refundTransactions = null;
    },
    [getRefundDetails.rejected]: (state, { payload }) => {
      state.isRefundTransactionsLoading = false;
      state.isRefundTransactionsError = true;
      state.refundTransactionsError = payload.message;
      state.refundTransactions = null;
    },
    [getRefundDetails.fulfilled]: (state, { payload }) => {
      state.isRefundTransactionsLoading = false;

      if (payload.status !== 'SUCCESS' || !payload?.data) {
        state.isRefundTransactionsError = true;
        state.refundTransactionsError = 'No data found';
        state.refundTransactions = null;
        return;
      }

      state.isRefundTransactionsError = false;
      state.refundTransactions = payload.data;
    },
  },
});

export const paymentSearchActions = searchOrderSlice.actions;

export const selectSearchParams = (state) =>
  state.paymentSearch.searchOrder.searchParams;

export const selectIsOrderDetailsLoading = (state) =>
  state.paymentSearch.searchOrder.isOrderDetailsLoading;
export const selectOrderDetails = (state) =>
  state.paymentSearch.searchOrder.orderDetails;
export const selectOrderDetailsError = (state) =>
  state.paymentSearch.searchOrder.orderDetailsError;

export const selectIsPaymentTransactionsLoading = (state) =>
  state.paymentSearch.searchOrder.isPaymentTransactionsLoading;
export const selectPaymentTransactions = (state) =>
  state.paymentSearch.searchOrder.paymentTransactions;
export const selectPaymentTransactionsError = (state) =>
  state.paymentSearch.searchOrder.paymentTransactionsError;

export const selectisRefundTransactionsLoading = (state) =>
  state.paymentSearch.searchOrder.isRefundTransactionsLoading;
export const selectRefundTransactions = (state) =>
  state.paymentSearch.searchOrder.refundTransactions;
export const selectRefundTransactionsError = (state) =>
  state.paymentSearch.searchOrder.refundTransactionsError;

export default searchOrderSlice.reducer;
